import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Save, User, ImagePlus } from 'lucide-react';
import { DashboardHeader } from '../components/DashboardHeader';
import { PhoneCodeSelect } from '../components/PhoneCodeSelect';
import { apiUrl } from '../utils/api';

interface ProfileData {
  id: string;
  email: string;
  fullName: string;
  phoneNumber: string;
  profileImageUrl: string | null;
  title: string | null;
  bio: string | null;
}

export function ProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');

  const [form, setForm] = useState({
    fullName: '',
    phoneCode: '+57',
    phoneNumber: '',
    title: '',
    bio: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    try {
      const res = await fetch(apiUrl('/api/profile'), {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setProfile(data.data);
        // Parse phone number to extract code and number
        const phone = data.data.phoneNumber || '';
        let code = '+57';
        let number = phone;
        const codeMatch = phone.match(/^(\+\d{1,3})\s*(.*)/);
        if (codeMatch) {
          code = codeMatch[1];
          number = codeMatch[2];
        }
        setForm({
          fullName: data.data.fullName || '',
          phoneCode: code,
          phoneNumber: number,
          title: data.data.title || '',
          bio: data.data.bio || '',
        });
      }
    } catch {
      setMessage('Error al cargar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsSaving(true);
    setMessage('');

    try {
      const res = await fetch(apiUrl('/api/profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          fullName: form.fullName,
          phoneNumber: `${form.phoneCode} ${form.phoneNumber}`,
          title: form.title,
          bio: form.bio,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setProfile(data.data);
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          localStorage.setItem('user', JSON.stringify({ ...user, fullName: form.fullName }));
        }
        setMessage('✓ Perfil actualizado');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.error?.message || 'Error al guardar');
      }
    } catch {
      setMessage('Error de conexión');
    } finally {
      setIsSaving(false);
    }
  };

  const compressImage = (file: File, maxSizeKB: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Resize to max 300x300 for profile pics
          const maxDim = 300;
          let w = img.width;
          let h = img.height;
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = (h / w) * maxDim; w = maxDim; }
            else { w = (w / h) * maxDim; h = maxDim; }
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, w, h);

          // Try progressively lower quality
          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob((blob) => {
              if (!blob) return reject(new Error('Compression failed'));
              if (blob.size <= maxSizeKB * 1024 || quality <= 0.1) {
                resolve(blob);
              } else {
                quality -= 0.1;
                tryCompress();
              }
            }, 'image/jpeg', quality);
          };
          tryCompress();
        };
        img.onerror = reject;
        img.src = e.target!.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (file: File) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsUploading(true);
    setMessage('');

    try {
      // Compress to 200KB max, 300x300
      const compressed = await compressImage(file, 200);

      const fd = new FormData();
      fd.append('profileImage', compressed, 'profile.jpg');

      const res = await fetch(apiUrl('/api/profile/image'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setProfile(prev => prev ? { ...prev, profileImageUrl: data.data.profileImageUrl } : null);
        setMessage('✓ Foto actualizada');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.error?.message || 'Error al subir imagen');
      }
    } catch {
      setMessage('Error de conexión');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageSelect(file);
    e.target.value = '';
  };

  const handleTakePhoto = () => {
    // Open camera using capture attribute
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'user'; // Front camera
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) handleImageSelect(file);
    };
    input.click();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardHeader userName="" onCreateCampaign={() => navigate('/campaigns/create')} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader userName={form.fullName} onCreateCampaign={() => navigate('/campaigns/create')} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Mi Perfil</h1>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left: Photo section */}
            <div className="lg:w-80 p-8 flex flex-col items-center bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200">
              <div className="relative mb-4">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center ring-4 ring-white shadow-lg">
                  {profile?.profileImageUrl ? (
                    <img src={profile.profileImageUrl} alt="Perfil" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-14 h-14 text-gray-400" />
                  )}
                </div>
                {isUploading && (
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>

              {/* Photo buttons */}
              <div className="flex flex-col gap-2 w-full max-w-[180px]">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors w-full"
                >
                  <ImagePlus className="w-4 h-4" />
                  <span>Subir foto</span>
                </button>
                <button
                  type="button"
                  onClick={handleTakePhoto}
                  className="flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors w-full"
                >
                  <Camera className="w-4 h-4" />
                  <span>Tomar foto</span>
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />

              <p className="text-[10px] text-gray-400 mt-3 text-center">
                Se comprime automáticamente a 200KB
              </p>
            </div>

            {/* Right: Form */}
            <div className="flex-1 p-8">
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título / Cargo</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ej: Pastor, Líder comunitario..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    rows={3}
                    maxLength={500}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    placeholder="Ej: Pastor iglesia Ipuc Suárez Cauca"
                  />
                  <p className="text-xs text-gray-400 mt-0.5 text-right">{form.bio.length}/500</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <div className="flex gap-2">
                    <div className="w-32 flex-shrink-0">
                      <PhoneCodeSelect
                        value={form.phoneCode}
                        onChange={(code) => setForm({ ...form, phoneCode: code })}
                      />
                    </div>
                    <input
                      type="tel"
                      value={form.phoneNumber}
                      onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="300 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">El email no se puede cambiar</p>
                </div>

                {message && (
                  <div className={`p-3 rounded-lg text-sm font-medium ${
                    message.includes('Error') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
                  }`}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSaving}
                  className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center space-x-2 text-sm transition-colors"
                >
                  {isSaving ? <span>Guardando...</span> : <><Save className="w-4 h-4" /><span>Guardar Cambios</span></>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
